import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';

/**
 * Entidades de programacion y ejecucion (Fase 2).
 * Los ejercicios se referencian por `slug` del catalogo en codigo (ADR-024).
 */

// --------------------------------------------------------------------------
// Cuando entrena el atleta
// --------------------------------------------------------------------------

/**
 * Cuando se entrena, con el grano que de verdad se conoce.
 *
 * NO es una hora. Es el turno, y el motor no puede fingir que sabe mas. La
 * evolucion a hora exacta cabe aqui sin romper nada —seria un campo mas junto a
 * este, y este pasaria a derivarse—.
 *
 * VIVIO EN `nutrition.ts` HASTA EL 21/09/2026, y no por descuido: nacio para
 * colocar las comidas alrededor de la sesion, asi que se escribio donde estaba
 * su primer consumidor. Pero el dato es del ENTRENAMIENTO —lo persiste
 * `training_sessions.training_time_of_day`, no ninguna tabla de nutricion— y al
 * retirar la nutricion se habria ido con ella por vivir en su fichero. Es
 * exactamente el aviso del encargo: no eliminar modulos por su nombre, sino
 * mirar primero que hay dentro.
 */
export const trainingTimeOfDaySchema = z.enum(['morning', 'afternoon']);
export type TrainingTimeOfDay = z.infer<typeof trainingTimeOfDaySchema>;

// --------------------------------------------------------------------------
// Programacion
// --------------------------------------------------------------------------

/**
 * La ESTRUCTURA de entrenamiento de un bloque.
 *
 * Se llama `blockPhase` por la columna `training_blocks.phase`, que no se
 * renombra: el nombre de una columna no vale una migracion de datos.
 *
 * QUE CAMBIO EL 21/09/2026, Y POR QUE
 *
 * Cuatro de los seis valores describian BALANCE ENERGETICO y no estructura de
 * entrenamiento: `fat_loss`, `recomposition`, `muscle_gain` y `maintenance`
 * nombran lo que se come, no como se entrena. El arbol que los elegia leia
 * grasa corporal, cintura y adherencia nutricional, que es exactamente lo que
 * el §6 del encargo prohibe: «no utilices automaticamente un porcentaje de
 * grasa, una cintura o una variacion de peso para decidir una dieta o imponer
 * una fase».
 *
 * Los valores no se borran —hay filas guardadas con ellos, y un enum que deja
 * de validar lo que ya existe rompe la lectura de todo el historial—, se
 * reparten en dos listas:
 *
 *   - `SELECTABLE_BLOCK_KINDS`: lo que un bloque NUEVO puede ser.
 *   - Heredados: `fat_loss` y `recomposition`. Ya no los genera nadie, siguen
 *     validando al leer y siguen teniendo rampa para pintarse. Es el mismo
 *     patron que `extra` en `mealSlotSchema`.
 *
 * `muscle_gain` SE QUEDA como seleccionable y significa lo que siempre dijo su
 * etiqueta —«Ganancia muscular»—, que es el bloque de desarrollo de hipertrofia
 * del §6. No se renombra a `hypertrophy` porque seria una migracion de datos
 * para cambiar una palabra por su sinonimo.
 *
 * `specialization` es nuevo: el §6 lo nombra expresamente y las prioridades
 * musculares ya existen en el modelo, asi que habia estructura sin nombre.
 * Entra con migracion NUEVA que ensancha el CHECK de la 0003, que no se toca.
 */
export const blockPhaseSchema = z.enum([
  'readaptation',
  'recomposition',
  'fat_loss',
  'maintenance',
  'muscle_gain',
  'specialization',
  'transition',
]);

/**
 * Lo que un bloque nuevo puede ser, en el orden en que se ofrece.
 *
 * Esta aqui y no en el dominio por el mismo motivo que `SELECTABLE_MEAL_SLOTS`:
 * se PERSISTE, y el CHECK de la migracion repite la lista. Una segunda copia en
 * el dominio se separaria de la columna sin que nada avisara.
 */
export const SELECTABLE_BLOCK_KINDS = [
  'readaptation',
  'muscle_gain',
  'specialization',
  'maintenance',
  'transition',
] as const;

/** Los que solo se leen: existen filas con ellos y nadie los genera ya. */
export const LEGACY_BLOCK_KINDS = ['fat_loss', 'recomposition'] as const;

export type SelectableBlockKind = (typeof SELECTABLE_BLOCK_KINDS)[number];

/**
 * El OBJETIVO DEL ATLETA (§6.1 del encargo). Lo declara el usuario.
 *
 * Es distinto del bloque: el objetivo dura, el bloque es como se persigue este
 * trimestre. Separarlos es lo que permite que «mantener el plan» sea una
 * decision valida sin que eso signifique cambiar de objetivo.
 *
 * NO SE INFIERE DE NINGUNA MEDIDA. Hasta el 21/09/2026 el motor tenia un
 * `muscleGainGoalActive` que su unico productor pasaba en `false` fijo, con un
 * comentario que decia la verdad —«no hay donde declarar un objetivo de masa»—:
 * la rama de ganancia muscular era INALCANZABLE en la aplicacion. Un campo
 * declarado y una pantalla que lo escriba es lo que cierra eso.
 */
export const athleteGoalSchema = z.enum([
  /** Desarrollar masa muscular. */
  'muscle_gain',
  /** Mejorar rendimiento en los patrones principales. */
  'performance',
  /** Sostener lo conseguido. */
  'maintenance',
]);
export type AthleteGoal = z.infer<typeof athleteGoalSchema>;

/**
 * CONTEXTO declarado de recuperacion (§6.2 del encargo).
 *
 * Informacion util para ajustar expectativas y volumen, **sin prescribir
 * nutricion**. `reduced` es lo que el usuario dice de si mismo —«estoy en
 * deficit», «duermo mal», «temporada de examenes»—, no lo que una bascula
 * deduce de el.
 *
 * MODULA, NO IMPONE: acota cuanto puede subir el volumen, y no elige el bloque.
 * Esa es toda la diferencia con el arbol que habia, donde una cinta metrica
 * decidia la fase por su cuenta.
 */
export const recoveryContextSchema = z.enum(['normal', 'reduced']);
export type RecoveryContext = z.infer<typeof recoveryContextSchema>;

export const splitSchema = z.enum([
  'upper_lower',
  'push_pull_legs',
  'full_body',
  'torso_leg',
  'custom',
]);

export type BlockPhase = z.infer<typeof blockPhaseSchema>;
export type Split = z.infer<typeof splitSchema>;

export const blockStatusSchema = z.enum(['planned', 'active', 'completed', 'aborted']);

/**
 * Vocabulario de musculos.
 *
 * Vive aqui, y no en el dominio, desde que se persiste: `muscle_priorities` es
 * un jsonb cuyas CLAVES son estos nombres, asi que forman parte del contrato
 * con la base de datos igual que `equipmentKind`. El dominio lo reexporta para
 * que el resto del codigo no note el cambio, pero la fuente unica es esta: dos
 * listas separadas acabarian divergiendo sin que nada avisara.
 */
export const muscleSchema = z.enum([
  'pectoral',
  'dorsal',
  'trapecio',
  'romboides',
  'deltoide_anterior',
  'deltoide_lateral',
  'deltoide_posterior',
  'biceps',
  'triceps',
  'antebrazo',
  'cuadriceps',
  'isquios',
  'gluteo',
  'aductores',
  'gemelo',
  'soleo',
  'core',
  'lumbar',
]);

export type Muscle = z.infer<typeof muscleSchema>;

export const musclePrioritySchema = z.enum(['high', 'normal', 'low']);

export type MusclePriority = z.infer<typeof musclePrioritySchema>;

/**
 * Prioridades declaradas para un bloque. Solo aparecen los musculos que no son
 * `normal`: guardar dieciocho claves con el valor por defecto engordaria cada
 * fila sin decir nada.
 *
 * `partialRecord` y no `record`: en Zod 4 un `record` con clave de enumeracion
 * es EXHAUSTIVO y exigiria los dieciocho musculos en cada bloque. Verificado
 * con zod 4.4.3 antes de escribir esto. `partialRecord` admite el mapa parcial
 * y sigue rechazando una clave que no sea un musculo.
 */
export const musclePrioritiesSchema = z.partialRecord(muscleSchema, musclePrioritySchema);

export type MusclePriorities = z.infer<typeof musclePrioritiesSchema>;

/**
 * Objetivo de series efectivas semanales por musculo (§4.3).
 *
 * `partialRecord` por el mismo motivo que las prioridades: solo aparecen los
 * musculos con objetivo declarado. El resto se materializa con la plantilla.
 *
 * El tope de 40 no es una banda de entrenamiento —la mas alta del §4.2 llega a
 * 22— sino un limite de cordura que frena un valor corrupto llegado de otro
 * dispositivo antes de que se convierta en series prescritas.
 */
export const volumeTargetsSchema = z.partialRecord(muscleSchema, z.number().min(0).max(40));

export type VolumeTargets = z.infer<typeof volumeTargetsSchema>;

/**
 * Un hueco de sesion tal y como se GUARDA: un rol y los candidatos en orden de
 * preferencia. El primero es el que se usa si su equipamiento existe.
 */
export const sessionSlotSchema = z.object({
  role: z.enum(['main', 'secondary', 'accessory']),
  candidates: z.array(z.string().min(1).max(64)).min(1).max(8),
});

export const plannedSessionSchema = z.object({
  name: z.string().min(1).max(40),
  objective: z.string().min(1).max(140),
  orderInWeek: z.number().int().min(0).max(6),
  slots: z.array(sessionSlotSchema).min(1).max(12),
});

/**
 * La estructura de sesiones de un bloque, cuando NO es la de la plantilla.
 *
 * POR QUE SE GUARDA, Y NO SE VUELVE A PEDIR
 *
 * `ensureWeekMaterialized` no lee el bloque guardado: lo RECONSTRUYE con
 * `buildPhaseBlock`, apoyandose en que construir es determinista dado el
 * equipamiento. Esa invariante es la que permite que anadir una maquina se note
 * en la semana siguiente sin migrar nada.
 *
 * Una estructura propuesta por la IA rompe esa condicion: la semana 1 saldria
 * con las sesiones que diseño el modelo y la semana 2 con las cuatro de
 * `splits.ts`, porque la reconstruccion no tiene forma de saber que hubo otra
 * cosa. El bloque cambiaria de forma a mitad sin que nadie lo pidiera, y
 * plausiblemente, que es como no se ve.
 *
 * Asi que el plan viaja con el bloque. Es la misma razon por la que
 * `volumeTargets` se guarda y no se recalcula.
 *
 * `null` = **construido con la plantilla determinista de su fase**, que es
 * exactamente lo que le pasa a todo bloque anterior a esta columna. Nula a
 * proposito y sin `default`: `alter table ... add column ... default` no toca
 * `sync_seq`, asi que un valor por defecto solo existiria en el servidor
 * (R-16). Nulo significa lo mismo en los dos lados sin reparar nada, que es el
 * patron de `recipe_snapshot` en la 0021 y de `training_time_of_day` en la 0027.
 */
export const sessionPlanSchema = z.array(plannedSessionSchema).min(1).max(6);

export type SessionSlot = z.infer<typeof sessionSlotSchema>;
export type PlannedSession = z.infer<typeof plannedSessionSchema>;
export type SessionPlan = z.infer<typeof sessionPlanSchema>;

/**
 * Cuándo puede entrenar el atleta (test inicial, docs/TEST_INICIAL.md §2.1).
 *
 * Hasta el 23/09/2026 no existía: las sesiones por semana salían de la
 * plantilla y los días estaban fijos en el código (lunes, martes, jueves y
 * viernes). `days` son días ISO —1 = lunes … 7 = domingo—, sin repetir, y su
 * longitud ES el número de sesiones: no se guarda aparte para que no puedan
 * contradecirse.
 *
 * Los topes son los de `SAFETY_LIMITS.session` (5 sesiones de fuerza, 90
 * minutos). El mínimo de 60 minutos es deliberado: el producto excluye un
 * «modo de 45 minutos» (PRODUCT_SPEC.md). Que no haya más de tres días seguidos
 * lo comprueba el dominio (`validateAvailability`), porque depende del orden y
 * no cabe en un esquema.
 */
export const isoWeekdaySchema = z.number().int().min(1).max(7);

export const availabilitySchema = z.object({
  days: z
    .array(isoWeekdaySchema)
    .min(2)
    .max(5)
    .refine((dias) => new Set(dias).size === dias.length, 'días repetidos'),
  maxSessionMinutes: z.union([z.literal(60), z.literal(75), z.literal(90)]),
});

export type Availability = z.infer<typeof availabilitySchema>;

export const trainingBlockSchema = syncColumnsSchema.extend({
  name: z.string().min(1).max(80),
  phase: blockPhaseSchema,
  split: splitSchema,
  plannedWeeks: z.number().int().min(1).max(16),
  actualWeeks: z.number().int().min(0).max(16).nullable(),
  startedOn: localDateSchema,
  endedOn: localDateSchema.nullable(),
  status: blockStatusSchema,
  objectives: z.array(z.string()),
  exitMetrics: z.array(z.string()),
  sessionsPerWeek: z.number().int().min(1).max(7),
  maxSessionMinutes: z.number().int().min(20).max(180),
  /**
   * Prioridades del bloque (docs/TRAINING_ENGINE.md §4.4). Con valor por
   * defecto para que los bloques creados antes de existir esta columna sigan
   * validando: un bloque sin prioridades declaradas es un bloque sin enfasis,
   * que es exactamente lo que habia.
   */
  musclePriorities: musclePrioritiesSchema.default({}),
  /**
   * Objetivo de volumen vigente (§4.3). Con valor por defecto para que los
   * bloques creados antes de existir esta columna sigan validando: un bloque
   * sin objetivos se materializa con la plantilla, que es lo que habia.
   */
  volumeTargets: volumeTargetsSchema.default({}),
  /**
   * Las sesiones que diseño la IA, o `null` si las puso la plantilla.
   *
   * Ver `sessionPlanSchema`: se guarda porque la rematerializacion reconstruye
   * el bloque en vez de leerlo, y sin esto el diseño se perderia en la semana 2.
   *
   * `.nullable().default(null)` para que los bloques creados antes de la
   * migracion 0030 sigan validando. Su `null` no es «no se sabe»: es «lo puso
   * la plantilla», que es la verdad.
   */
  sessionPlan: sessionPlanSchema.nullable().default(null),
  /**
   * La disponibilidad con la que NACIÓ el bloque (0034), copiada del perfil.
   *
   * Copia y no referencia, por lo mismo que `sessionPlan`: las semanas 2 en
   * adelante se RECONSTRUYEN, y reconstruirlas con la disponibilidad actual del
   * perfil cambiaría la estructura del bloque a mitad —otros días, otro número
   * de sesiones— sin que nadie lo aprobara. Un cambio de disponibilidad entra
   * en el bloque siguiente. `null` = un bloque anterior al test inicial: los
   * cuatro días de siempre.
   */
  availability: availabilitySchema.nullable().default(null),
});

export type TrainingBlock = z.infer<typeof trainingBlockSchema>;

export const weekIntentSchema = z.enum([
  'accumulation',
  'intensification',
  'deload',
  'reintroduction',
]);

export type WeekIntent = z.infer<typeof weekIntentSchema>;

export const trainingWeekSchema = syncColumnsSchema.extend({
  blockId: z.uuid(),
  weekIndex: z.number().int().min(1).max(16),
  weekStart: localDateSchema,
  intent: weekIntentSchema,
  status: z.enum(['planned', 'active', 'completed']),
});

export type TrainingWeek = z.infer<typeof trainingWeekSchema>;

export const sessionKindSchema = z.enum(['strength', 'cardio', 'rest', 'strength_plus_cardio']);

export const sessionStatusSchema = z.enum([
  'planned',
  'in_progress',
  'completed',
  'missed',
  'rescheduled',
  'skipped',
]);

export type SessionKind = z.infer<typeof sessionKindSchema>;
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

export const trainingSessionSchema = syncColumnsSchema.extend({
  weekId: z.uuid(),
  blockId: z.uuid(),
  scheduledDate: localDateSchema,
  sessionKind: sessionKindSchema,
  name: z.string().min(1).max(80),
  objective: z.string().max(200),
  orderInWeek: z.number().int().min(0).max(6),
  estimatedMinutes: z.number().int().min(0).max(180),
  status: sessionStatusSchema,
  rescheduledFrom: localDateSchema.nullable(),
  /**
   * Turno de la sesion: manana o tarde. `null` = no se ha dicho, y entonces
   * manda la preferencia habitual (`resolveTrainingTimeOfDay`).
   *
   * NULA A PROPOSITO, y no con un `default`. La columna llego en la migracion
   * 0027, y `alter table ... add column ... default` no toca `sync_seq`: las
   * sesiones que ya existian no se vuelven a bajar, asi que un valor por
   * defecto en la columna solo estaria en el servidor (R-16). Nula significa
   * exactamente lo que le pasa a una sesion anterior a este cambio —nadie ha
   * dicho cuando se entrena— y cliente y servidor coinciden sin reparar nada.
   * Es el mismo patron que `planned_meals.recipe_snapshot` en la 0021.
   *
   * Lo que NO es: una hora. Ver `trainingTimeOfDaySchema`.
   */
  trainingTimeOfDay: trainingTimeOfDaySchema.nullable().default(null),
  /** Bloqueo suave mientras se ejecuta (docs/SYNC_ENGINE.md §5.2). */
  lockedByDevice: z.string().max(64).nullable(),
  lockedAt: z.iso.datetime({ offset: true }).nullable(),
});

export type TrainingSession = z.infer<typeof trainingSessionSchema>;

export const exerciseRoleSchema = z.enum(['main', 'secondary', 'accessory', 'finisher']);

export const techniqueSchema = z.enum([
  'straight',
  'top_backoff',
  'drop',
  'rest_pause',
  'myo_reps',
  'superset',
]);

export const sessionExerciseSchema = syncColumnsSchema.extend({
  sessionId: z.uuid(),
  /** Slug del catalogo en codigo. */
  exerciseSlug: z.string().min(1).max(80),
  equipmentId: z.uuid().nullable(),
  orderIndex: z.number().int().min(1).max(30),
  role: exerciseRoleSchema,
  technique: techniqueSchema,
  supersetGroup: z.number().int().min(1).max(10).nullable(),
  targetRestSeconds: z.number().int().min(0).max(600),
  notes: z.string().max(300).nullable(),
  substitutedFromSlug: z.string().max(80).nullable(),
});

export type SessionExercise = z.infer<typeof sessionExerciseSchema>;

export const setTypeSchema = z.enum(['effective', 'top', 'backoff', 'drop', 'rest_pause', 'myo']);

/** El dominio contabiliza volumen por tipo de serie: necesita el tipo, no el esquema. */
export type SetType = z.infer<typeof setTypeSchema>;

export const setPrescriptionSchema = syncColumnsSchema.extend({
  sessionExerciseId: z.uuid(),
  setIndex: z.number().int().min(1).max(20),
  setType: setTypeSchema,
  targetRepsMin: z.number().int().min(1).max(50),
  targetRepsMax: z.number().int().min(1).max(50),
  targetRir: z.number().min(0).max(6),
  targetLoadKg: z.number().min(0).max(1000).nullable(),
  restSeconds: z.number().int().min(0).max(600),
});

export type SetPrescription = z.infer<typeof setPrescriptionSchema>;

/** Tabla separada a proposito: nunca cuenta como volumen efectivo (ADR-013). */
export const warmupPrescriptionSchema = syncColumnsSchema.extend({
  sessionExerciseId: z.uuid(),
  orderIndex: z.number().int().min(1).max(10),
  loadKg: z.number().min(0).max(1000),
  reps: z.number().int().min(1).max(30),
  restSeconds: z.number().int().min(0).max(300),
  isCompleted: z.boolean(),
});

export type WarmupPrescription = z.infer<typeof warmupPrescriptionSchema>;

// --------------------------------------------------------------------------
// Recuperacion
// --------------------------------------------------------------------------

export const bodyRegionSchema = z.enum([
  'chest',
  'upper_back',
  'lower_back',
  'shoulders_front',
  'shoulders_side',
  'shoulders_rear',
  'biceps',
  'triceps',
  'forearms',
  'abs',
  'quads',
  'hamstrings',
  'glutes',
  'calves',
  'hip',
  'knee',
  'ankle',
  'elbow',
  'wrist',
  'neck',
]);

/** Lista cerrada, en el mismo orden que `SYMPTOM_CODES` del dominio. */
export const symptomCodeSchema = z.enum([
  'chest_pain',
  'breathing_difficulty',
  'neuro_symptoms',
  'fainting',
  'joint_pain',
]);

export type SymptomCodeName = z.infer<typeof symptomCodeSchema>;

export const sorenessAreaSchema = z.object({
  region: bodyRegionSchema,
  intensity: z.number().int().min(1).max(5),
  /** Nunca categorias diagnosticas. */
  kind: z.enum(['doms', 'stiffness', 'discomfort']),
});

export const readinessCheckinSchema = syncColumnsSchema.extend({
  checkinAt: z.iso.datetime({ offset: true }),
  forDate: localDateSchema,
  sleepHours: z.number().min(0).max(16),
  sleepQuality: z.number().int().min(1).max(5),
  energy: z.number().int().min(1).max(5),
  motivation: z.number().int().min(1).max(5),
  stress: z.number().int().min(1).max(5),
  soreness: z.number().int().min(1).max(5),
  sorenessAreas: z.array(sorenessAreaSchema),
  /**
   * Sintomas marcados por el usuario, de una lista cerrada. Nunca texto libre:
   * interpretar prosa para decidir si alguien deja de entrenar seria una
   * inferencia que este sistema no debe hacer (docs/SECURITY_PRIVACY.md §6.1).
   */
  reportedSymptoms: z.array(symptomCodeSchema).default([]),
  readinessScore: z.number().min(0).max(100),
  scoreVersion: z.string().min(1).max(40),
});

export type ReadinessCheckin = z.infer<typeof readinessCheckinSchema>;
export type SorenessArea = z.infer<typeof sorenessAreaSchema>;
export type BodyRegionName = z.infer<typeof bodyRegionSchema>;

// --------------------------------------------------------------------------
// Ejecucion
// --------------------------------------------------------------------------

export const workoutLogSchema = syncColumnsSchema.extend({
  sessionId: z.uuid().nullable(),
  startedAt: z.iso.datetime({ offset: true }),
  endedAt: z.iso.datetime({ offset: true }).nullable(),
  readinessCheckinId: z.uuid().nullable(),
  /** Prescripcion CONGELADA al empezar (requisito §61). */
  planSnapshot: z.record(z.string(), z.unknown()),
  sessionDifficulty: z.number().int().min(1).max(5).nullable(),
  sessionQuality: z.number().int().min(1).max(5).nullable(),
  /**
   * Series efectivas de la sesion, con la contabilidad del §4.1: un drop pesa
   * 0,5 y un rest-pause o un myo, 1,5. Por eso NO es entero (BUG-005): la
   * columna es `numeric(5,1)` desde la migracion 0024, porque redondear 7,5
   * para que cupiera en un `smallint` era guardar otro numero falso.
   */
  totalEffectiveSets: z.number().min(0).max(200).multipleOf(0.5),
  status: z.enum(['in_progress', 'completed', 'abandoned']),
});

export type WorkoutLog = z.infer<typeof workoutLogSchema>;

export const performedSetSchema = syncColumnsSchema.extend({
  workoutLogId: z.uuid(),
  sessionExerciseId: z.uuid().nullable(),
  setPrescriptionId: z.uuid().nullable(),
  /** Copiados, no solo referenciados: el historial no depende de la plantilla. */
  exerciseSlug: z.string().min(1).max(80),
  equipmentId: z.uuid().nullable(),
  setIndex: z.number().int().min(1).max(20),
  setType: setTypeSchema,
  loadKg: z.number().min(0).max(1000),
  reps: z.number().int().min(1).max(100),
  rir: z.number().min(0).max(10),
  e1rmKg: z.number().min(0).max(1000),
  formulaVersion: z.string().min(1).max(40),
  recommendedLoadKg: z.number().min(0).max(1000).nullable(),
  wasOverride: z.boolean(),
  performedAt: z.iso.datetime({ offset: true }),
});

export type PerformedSet = z.infer<typeof performedSetSchema>;

export const restEventSchema = syncColumnsSchema.extend({
  workoutLogId: z.uuid(),
  afterPerformedSetId: z.uuid().nullable(),
  plannedSeconds: z.number().int().min(0).max(900),
  actualSeconds: z.number().int().min(0).max(3600),
  startedAt: z.iso.datetime({ offset: true }),
});

export type RestEvent = z.infer<typeof restEventSchema>;

// --------------------------------------------------------------------------
// Inteligencia de entrenamiento
// --------------------------------------------------------------------------

export const loadRecommendationSchema = syncColumnsSchema.extend({
  workoutLogId: z.uuid(),
  sessionExerciseId: z.uuid().nullable(),
  setIndex: z.number().int().min(1).max(20),
  recommendedLoadKg: z.number().min(0).max(1000).nullable(),
  rawLoadKg: z.number().min(0).max(1000).nullable(),
  targetReps: z.number().int().min(1).max(50),
  targetRir: z.number().min(0).max(6),
  inputs: z.record(z.string(), z.unknown()),
  strategy: z.string().min(1).max(40),
  reasonCodes: z.array(z.string()),
  engineVersion: z.string().min(1).max(40),
  confidence: z.number().min(0).max(1),
});

export type LoadRecommendationRow = z.infer<typeof loadRecommendationSchema>;

export const progressionStrategySchema = z.enum([
  'double_progression',
  'load_progression',
  'rep_progression',
  'top_backoff',
  'rir_ramp',
]);

export const progressionStateSchema = syncColumnsSchema.extend({
  exerciseSlug: z.string().min(1).max(80),
  equipmentId: z.uuid().nullable(),
  strategy: progressionStrategySchema,
  currentE1rmKg: z.number().min(0).max(1000).nullable(),
  /** Tendencia del e1RM en %/semana sobre la ventana de 30 dias. */
  e1rmTrendMonthly: z.number().min(-100).max(100).nullable(),
  consecutiveTopRange: z.number().int().min(0).max(50),
  stallCounter: z.number().int().min(0).max(50),
  overrideBiasPct: z.number().min(-0.05).max(0.05),
  lastUpdatedSession: z.uuid().nullable(),
});

export type ProgressionStateRow = z.infer<typeof progressionStateSchema>;

// --------------------------------------------------------------------------
// Registro de decisiones (Fase 3)
// --------------------------------------------------------------------------

/**
 * Los tres vocabularios de una decision viven aqui, y no en el dominio, porque
 * se guardan en columnas con `check`: son parte del contrato con la base de
 * datos. El dominio los reexporta.
 */
export const decisionTypeSchema = z.enum([
  'block_start',
  'phase_change',
  'split_change',
  'volume_change',
  'deload',
  'load_adjust',
  'exercise_substitution',
  'week_reorganization',
  'priority_change',
  // Los cuatro escalones del §9 comparten tipo a proposito: ver la migracion
  // 0011, que explica por que no se reparten entre los que ya existian.
  'stall_response',
  // Revisar no obliga a cambiar (§11 del encargo), y no cambiar tambien se
  // registra: sin esto una semana revisada y una que nadie reviso dejaban el
  // mismo historial. Migracion 0032.
  'plan_kept',
]);

export type DecisionType = z.infer<typeof decisionTypeSchema>;

export const decisionSourceSchema = z.enum(['algorithm', 'claude', 'user']);

export type DecisionSource = z.infer<typeof decisionSourceSchema>;

export const approvalStatusSchema = z.enum([
  'not_required',
  'pending',
  'approved',
  'rejected',
  'expired',
]);

export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

/**
 * Registro central del motor de decisiones (docs/DATA_MODEL.md, requisito §62).
 *
 * No lleva columna de caducidad a proposito: el limite se deriva de
 * `decidedAt` mas `APPROVAL_EXPIRY_DAYS`. Guardar un valor calculado obligaria
 * a mantenerlo en dos sitios y a decidir que pasa con las filas antiguas si la
 * ventana cambia.
 */
export const trainingDecisionSchema = syncColumnsSchema.extend({
  decisionType: decisionTypeSchema,
  decidedAt: z.iso.datetime({ offset: true }),
  /** Entradas resumidas, no el historial completo. */
  inputs: z.record(z.string(), z.unknown()),
  previousValue: z.unknown(),
  newValue: z.unknown(),
  source: decisionSourceSchema,
  /** Motivo estructurado: se puede agrupar y contar sin analizar texto. */
  reasonCodes: z.array(z.string()),
  reasonText: z.string().max(500).nullable(),
  confidence: z.number().min(0).max(1),
  requiresApproval: z.boolean(),
  approvalStatus: approvalStatusSchema,
  approvedAt: z.iso.datetime({ offset: true }).nullable(),
  /**
   * Cuando la decision se llevo al plan de verdad. `null` = todavia no.
   *
   * Separado de `approvalStatus` a proposito: aprobar es un permiso, aplicar es
   * un hecho, y confundirlos es lo que hacia que el panel dijera «aplicado»
   * sobre algo que no habia tocado nada.
   */
  appliedAt: z.iso.datetime({ offset: true }).nullable().default(null),
  evaluateAfter: localDateSchema.nullable(),
  outcome: z.unknown(),
  outcomeEvaluatedAt: z.iso.datetime({ offset: true }).nullable(),
});

export type TrainingDecision = z.infer<typeof trainingDecisionSchema>;

// --------------------------------------------------------------------------
// Cardio (docs/DATA_MODEL.md §11)
// --------------------------------------------------------------------------

export const cardioModalitySchema = z.enum(['continuous_run']);
export const cardioIntensitySchema = z.enum(['easy', 'moderate', 'threshold']);

export type CardioModality = z.infer<typeof cardioModalitySchema>;
export type CardioIntensity = z.infer<typeof cardioIntensitySchema>;

export const cardioPrescriptionSchema = syncColumnsSchema.extend({
  /** `null` cuando el cardio va en un dia sin sesion de fuerza, que es lo normal. */
  sessionId: z.uuid().nullable(),
  scheduledDate: localDateSchema,
  modality: cardioModalitySchema,
  targetMinutes: z.number().int().min(5).max(180),
  targetDistanceKm: z.number().positive().max(999).nullable(),
  targetIntensity: cardioIntensitySchema,
  /** Por que se prescribio esto: el usuario tiene derecho a saberlo. */
  rationale: z.string().max(200),
});

export type CardioPrescription = z.infer<typeof cardioPrescriptionSchema>;

export const cardioLogSchema = syncColumnsSchema.extend({
  /** `null` = cardio no prescrito. El §11 lo permite de forma explicita. */
  prescriptionId: z.uuid().nullable(),
  performedOn: localDateSchema,
  durationSeconds: z.number().int().min(60).max(36_000),
  distanceKm: z.number().positive().max(999).nullable(),
  /**
   * DERIVADO de duracion y distancia. Se guarda calculado para poder comparar
   * sin recalcular, pero la fuente son los dos anteriores: si discrepan, mandan
   * ellos.
   */
  paceSecondsPerKm: z.number().int().min(120).max(1800).nullable(),
  perceivedEffort: z.number().int().min(1).max(5).nullable(),
  notes: z.string().max(500).nullable(),
});

export type CardioLog = z.infer<typeof cardioLogSchema>;

/** Ritmo en segundos por kilometro, o `null` si no hay distancia que dividir. */
export function paceFrom(durationSeconds: number, distanceKm: number | null): number | null {
  if (distanceKm === null || distanceKm <= 0) return null;
  return Math.round(durationSeconds / distanceKm);
}
